/**
 * Road network for a raid grid — pure, seedless, DOM-free.
 *
 * The map used to draw a road cross on every tile, because the connector
 * directions came from "is this neighbour walkable", which is true almost
 * everywhere. Every tile therefore looked the same and the roads said nothing
 * about the place.
 *
 * A grid now gets ONE major route that bisects it edge to edge, wandering
 * rather than running straight, plus minor pathways branching off it until
 * most of the map is reachable from a road. A tile draws road only where the
 * network runs, and only toward the neighbours it actually continues to.
 *
 * The trunk becomes road ROOMS (fast travel) — see createRoadNetwork. The
 * minor pathways are a visual layer only: they run over whatever room is
 * already there, so encounter counts and raid duration are untouched.
 *
 * Directions carry their OWN rank, not the tile's. A trunk tile that anchors a
 * branch continues at full width along the trunk and leaves as a narrow lane
 * toward the branch, so the branch starts at the roadway rather than at the
 * tile edge.
 */

export const MAJOR = 'major';
export const MINOR = 'minor';

const DIRS = { n: [-1, 0], s: [1, 0], e: [0, 1], w: [0, -1] };
const OPPOSITE = { n: 's', s: 'n', e: 'w', w: 'e' };

/** Termini must sit this far apart across the axis, or the route reads straight. */
export const MIN_TERMINUS_OFFSET = 3;

const key = (row, col) => `${row},${col}`;

function shuffle(list, rand) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function dirBetween(a, b) {
  if (b.row === a.row - 1 && b.col === a.col) return 'n';
  if (b.row === a.row + 1 && b.col === a.col) return 's';
  if (b.row === a.row && b.col === a.col + 1) return 'e';
  if (b.row === a.row && b.col === a.col - 1) return 'w';
  return null;
}

/**
 * A wandering line of cells from one edge of the grid to the opposite one.
 *
 * The two ends are chosen up front, at least MIN_TERMINUS_OFFSET apart across
 * the axis, and the jogs are then dealt out to walk between them. Rolling each
 * jog independently (as an earlier version did) let a route come out nearly
 * straight whenever the rolls happened to cancel.
 */
function bisectingRoute(rows, cols, horizontal, rand) {
  const span = horizontal ? cols : rows;
  const cross = horizontal ? rows : cols;
  const offset = Math.min(MIN_TERMINUS_OFFSET, cross - 1);

  const slots = Math.max(1, span - 1);
  // A jog costs a slot, and a route with a jog in every slot is a staircase.
  // Half the slots is the ceiling — enough to wander, and it leaves a straight
  // stretch between every turn. Small grids are where this bites: seven columns
  // give six slots, and an unbounded drift wanted a turn in all of them.
  const maxJogs = Math.max(offset, Math.ceil(slots / 2));

  const startLane = Math.floor(rand() * cross);
  // land the far end at least `offset` away, and no further than the route has
  // slots to get there in
  const room = [];
  for (let lane = 0; lane < cross; lane++) {
    const away = Math.abs(lane - startLane);
    if (away >= offset && away <= maxJogs) room.push(lane);
  }
  const endLane = room.length ? room[Math.floor(rand() * room.length)] : (cross - 1 - startLane);

  // one jog per unit of drift, plus a cancelling pair when there is slack for
  // it, so the route wanders instead of walking a clean diagonal
  const drift = endLane - startLane;
  const steps = [];
  for (let i = 0; i < Math.abs(drift); i++) steps.push(Math.sign(drift));
  if (steps.length + 2 <= maxJogs && rand() < 0.5) { steps.push(1); steps.push(-1); }
  for (let i = steps.length - 1; i > 0; i--) {          // seeded shuffle
    const j = Math.floor(rand() * (i + 1));
    [steps[i], steps[j]] = [steps[j], steps[i]];
  }

  // One jog per slot, each in its own column. Letting a slot take two steps
  // allowed a +1 and a -1 to land together, which walked the route off its
  // lane and straight back onto the cell it had just left.
  //
  // The jogs are then SPREAD rather than scattered: the run is divided into one
  // bucket per jog and each jog placed somewhere inside its own bucket, which
  // leaves a straight stretch between turns. Picking slots at random let
  // several land in consecutive columns, and a route that turns in every column
  // crosses the map as a staircase — a pattern rather than a road.
  const at = new Map();
  const used = steps.slice(0, Math.min(slots, maxJogs));
  used.forEach((step, i) => {
    const lo = Math.floor((i * slots) / used.length);
    const hi = Math.max(lo + 1, Math.floor(((i + 1) * slots) / used.length));
    at.set(lo + Math.floor(rand() * (hi - lo)), [step]);
  });

  const cells = [];
  const push = (along, across) => cells.push(horizontal
    ? { row: across, col: along }
    : { row: along, col: across });

  let lane = startLane;
  for (let along = 0; along < span; along++) {
    push(along, lane);
    if (along === span - 1) break;
    for (const step of at.get(along) || []) {
      const next = lane + step;
      if (next < 0 || next >= cross) continue;
      lane = next;
      push(along, lane);
    }
  }
  return cells;
}

/**
 * Grow a pathway from a cell already on the network toward `target`.
 *
 * Aimed rather than random: growing in a random direction spends most of its
 * cells re-covering ground already beside the trunk, which left a third of the
 * map with no road within reach however large the budget got.
 */
function growToward(from, target, rows, cols, taken, rand, maxLen) {
  const free = c => c.row >= 0 && c.col >= 0 && c.row < rows && c.col < cols && !taken.has(key(c.row, c.col));
  const cells = [];
  let cur = from;
  let heading = null;                                   // the way the last step went
  let run = 0;                                          // and how far it has gone that way
  const hold = 2 + Math.floor(rand() * 3);              // cells to keep a heading before turning

  for (let i = 0; i < maxLen; i++) {
    const dr = Math.sign(target.row - cur.row), dc = Math.sign(target.col - cur.col);
    const byRow = { row: cur.row + dr, col: cur.col };
    const byCol = { row: cur.row, col: cur.col + dc };

    // Hold the current heading for a few cells before turning again. Choosing
    // the axis afresh every step walks a diagonal target as a staircase — a
    // turn in every single cell — which reads as a pattern, not a road.
    let order = null;
    if (heading && run < hold) {
      const keep = { row: cur.row + heading[0], col: cur.col + heading[1] };
      const stillHelps = heading[0] ? dr === heading[0] : dc === heading[1];
      if (stillHelps) order = [keep, byRow, byCol];
    }
    if (!order) {
      // close the larger gap first; the occasional swap keeps tracks from all
      // turning at the same point
      const wantRowFirst = Math.abs(target.row - cur.row) > Math.abs(target.col - cur.col);
      order = (rand() < 0.8 ? wantRowFirst : !wantRowFirst) ? [byRow, byCol] : [byCol, byRow];
    }
    const step = order.find(c => (c.row !== cur.row || c.col !== cur.col) && free(c));
    if (!step) break;

    const went = [step.row - cur.row, step.col - cur.col];
    if (heading && went[0] === heading[0] && went[1] === heading[1]) run++;
    else { heading = went; run = 1; }
    cells.push(step);
    taken.add(key(step.row, step.col));
    cur = step;
    if (cur.row === target.row && cur.col === target.col) break;
  }
  return cells;
}

/** Remove a dead-end cell, unwiring the neighbour that pointed at it. */
function dropLeaf(map, row, col) {
  const entry = map.get(key(row, col));
  if (!entry) return;
  for (const dir of Object.keys(entry.conn)) {
    const [dr, dc] = DIRS[dir];
    const other = map.get(key(row + dr, col + dc));
    if (other) delete other.conn[OPPOSITE[dir]];
  }
  map.delete(key(row, col));
}

/** Record that `cell` carries road heading `dir` at `rank`. MAJOR wins ties. */
function link(map, cell, dir, rank) {
  const k = key(cell.row, cell.col);
  const entry = map.get(k) || { row: cell.row, col: cell.col, conn: {}, rank };
  if (dir) entry.conn[dir] = entry.conn[dir] === MAJOR ? MAJOR : rank;
  if (rank === MAJOR) entry.rank = MAJOR;
  map.set(k, entry);
}

function linkRun(map, cells, rank) {
  for (let i = 0; i < cells.length - 1; i++) {
    const dir = dirBetween(cells[i], cells[i + 1]);
    if (!dir) continue;
    link(map, cells[i], dir, rank);
    link(map, cells[i + 1], OPPOSITE[dir], rank);
  }
}

/** How many steps each cell is from the nearest road, by flood fill. */
export function distanceField(rows, cols, isRoad) {
  const dist = Array.from({ length: rows }, () => new Array(cols).fill(Infinity));
  const queue = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (isRoad(row, col)) { dist[row][col] = 0; queue.push([row, col]); }
    }
  }
  for (let i = 0; i < queue.length; i++) {
    const [row, col] = queue[i];
    for (const [dr, dc] of Object.values(DIRS)) {
      const nr = row + dr, nc = col + dc;
      if (nr < 0 || nc < 0 || nr >= rows || nc >= cols) continue;
      if (dist[nr][nc] <= dist[row][col] + 1) continue;
      dist[nr][nc] = dist[row][col] + 1;
      queue.push([nr, nc]);
    }
  }
  return dist;
}

/** The furthest any cell sits from a road. */
export function maxRoadDistance(rows, cols, isRoad) {
  const dist = distanceField(rows, cols, isRoad);
  let worst = 0;
  for (const row of dist) for (const d of row) worst = Math.max(worst, d);
  return worst;
}

/** No cell on a raid map may sit further than this from a road or pathway. */
export const MAX_ROAD_DISTANCE = 2;

/** Cells a pathway must actually reach, not merely come within reach of. */
export const PORTAL_TYPES = new Set(['start', 'exit']);

/**
 * Hang pathways off the network until nothing is more than `maxDist` steps from
 * one.
 *
 * Run at generation AND again after the network is trimmed against the finished
 * grid: cutting a spur back from collapsed ground is what actually strands a
 * quarter of a map, and the generator never sees that grid.
 *
 * @param {Set} [opts.avoid]    cells a pathway may not enter — collapsed ground, water
 * @param {Set} [opts.noStopAt] cells a pathway may pass but not END on
 */
/**
 * Run one new pathway from the network to `target`.
 *
 * growToward only ever considers the two steps that close on its target, so an
 * obstruction sitting square between anchor and target stops it dead — which is
 * why this tries the nearest several road cells rather than only the nearest
 * one. Picking just the nearest meant re-trying the same hopeless pair forever
 * while a workable anchor sat a few cells along the row.
 *
 * @returns the cells laid, or null if no anchor could reach
 */
function joinToNetwork(map, rows, cols, rand, target, o) {
  // Never hang a new track off off-limits ground. A campsite is approached from
  // one side and left at that; running a second track off it would turn it into
  // a route straight through.
  const anchors = [...map.values()]
    .filter(c => !o.avoid.has(key(c.row, c.col)))
    .sort((a, b) => (Math.abs(a.row - target.row) + Math.abs(a.col - target.col))
      - (Math.abs(b.row - target.row) + Math.abs(b.col - target.col)))
    .slice(0, 8);
  for (const anchor of anchors) {
    const branch = growToward(anchor, target, rows, cols, o.taken, rand, o.maxLen);
    while (!o.relax && branch.length
      && o.noStopAt.has(key(branch[branch.length - 1].row, branch[branch.length - 1].col))) {
      const dropped = branch.pop();
      o.taken.delete(key(dropped.row, dropped.col));
    }
    if (!branch.length) continue;
    linkRun(map, [anchor, ...branch], MINOR);
    return { anchor, branch };
  }
  return null;
}

/**
 * The shortest legal run from the network to a cell, found properly.
 *
 * growToward is a greedy walk: both of the steps it will consider close the gap
 * to its target, so it cannot go AROUND anything. Where a cell's every direct
 * approach is off-limits it breaks on the first step and reports nothing, even
 * with clear ground one tile out. That is fine for coverage — a track that gets
 * partway still serves the ground it crossed — but not for a portal, which has
 * to actually be reached.
 *
 * Breadth-first out from the target across ground a track may use, stopping at
 * the first cell already on the network. Returns the run anchor-first, or null
 * when the cell is genuinely walled in.
 */
export function pathToNetwork(map, rows, cols, target, taken) {
  const cameFrom = new Map([[key(target.row, target.col), null]]);
  const queue = [target];
  while (queue.length) {
    const cur = queue.shift();
    for (const [dr, dc] of Object.values(DIRS)) {
      const row = cur.row + dr, col = cur.col + dc;
      if (row < 0 || col < 0 || row >= rows || col >= cols) continue;
      const k = key(row, col);
      if (cameFrom.has(k)) continue;
      const onNetwork = map.get(k);
      if (onNetwork) {
        const run = [];
        for (let at = cur; at; at = cameFrom.get(key(at.row, at.col))) run.push(at);
        return [onNetwork, ...run];          // anchor, then every step up to the target
      }
      if (taken.has(k)) continue;            // ground a track may not enter
      cameFrom.set(k, cur);
      queue.push({ row, col });
    }
  }
  return null;
}

export function fillToDistance(map, rows, cols, rand, maxDist, opts = {}) {
  const avoid = opts.avoid || new Set();
  const noStopAt = opts.noStopAt || new Set();
  const budget = opts.budget ?? Infinity;
  const taken = new Set([...map.keys(), ...avoid]);

  for (let pass = 0; pass < 200; pass++) {
    if (map.size >= budget) break;
    const dist = distanceField(rows, cols, (r, c) => map.has(key(r, c)));
    const far = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) if (dist[row][col] > maxDist) far.push({ row, col });
    }
    if (!far.length) break;

    // Aim at a cell that is not itself off-limits where possible: a track has
    // to end somewhere, and a track cannot end on ground it may not enter.
    const open = far.filter(c => !avoid.has(key(c.row, c.col)));
    const pool = shuffle(open.length ? open : far, rand);

    // The second sweep drops the no-dead-end-beside-rubble rule. Where a corner
    // is boxed in by collapsed ground, EVERY cell that could serve it is
    // alongside that ground, and the two rules cannot both hold. A track that
    // stops short of the rubble is a smaller wrong than a corner of the map
    // nothing reaches, so coverage wins — but only after the tidy route has
    // been ruled out for every stranded cell, not the first one.
    let placed = false;
    for (const relax of [false, true]) {
      for (const target of pool) {
        const laid = joinToNetwork(map, rows, cols, rand, target,
          { taken, avoid, noStopAt, relax, maxLen: maxDist + 6 });
        if (!laid) continue;
        const { anchor, branch } = laid;
        // Having spent the rule, spend no more of it than the map needs: pull
        // the relaxed track back off the rubble cell by cell for as long as
        // coverage survives without it.
        while (relax && branch.length
          && noStopAt.has(key(branch[branch.length - 1].row, branch[branch.length - 1].col))) {
          const tip = branch[branch.length - 1];
          const conn = { ...map.get(key(tip.row, tip.col))?.conn };
          dropLeaf(map, tip.row, tip.col);
          if (maxRoadDistance(rows, cols, (r, c) => map.has(key(r, c))) > maxDist) {
            linkRun(map, [branch[branch.length - 2] || anchor, tip], MINOR);
            const back = map.get(key(tip.row, tip.col));
            if (back) Object.assign(back.conn, conn);
            break;
          }
          taken.delete(key(tip.row, tip.col));
          branch.pop();
        }
        placed = branch.length > 0;
        if (placed) break;
      }
      if (placed) break;
    }
    if (!placed) break;                            // nothing left that can be joined
  }
}

/**
 * Build the network for a grid.
 * @param {Set} [opts.avoid] "row,col" keys the trunk must not touch (start/exit)
 * @returns {Map} "row,col" -> { row, col, conn: {dir: rank}, rank }
 */
export function generateRoadNetwork(rows, cols, rand = Math.random, opts = {}) {
  const avoid = opts.avoid || new Set();

  // Retry rather than steer around the entry and extraction points: they sit
  // on opposite map edges and so do the trunk's ends, so a collision is easy
  // to roll and trivial to re-roll.
  // Two things have to hold, and both are cheaper to re-roll than to steer:
  // the trunk must miss the entry and extraction points (they sit on opposite
  // map edges, and so do its ends), and its ends must be far enough apart
  // across the axis to read as a route rather than a straight line. The second
  // is not guaranteed by construction because a jog that would leave the grid
  // is dropped, which quietly eats some of the intended drift.
  let trunk = null;
  let horizontal = rand() < 0.5;
  for (let tries = 0; tries < 80; tries++) {
    const orient = tries === 0 ? horizontal : rand() < 0.5;
    const candidate = bisectingRoute(rows, cols, orient, rand);
    const cross = orient ? rows : cols;
    const first = candidate[0], last = candidate[candidate.length - 1];
    const spread = orient ? Math.abs(first.row - last.row) : Math.abs(first.col - last.col);
    if (spread < Math.min(MIN_TERMINUS_OFFSET, cross - 1)) continue;
    if (candidate.some(c => avoid.has(key(c.row, c.col)))) continue;
    trunk = candidate;
    horizontal = orient;
    break;
  }
  if (!trunk) trunk = bisectingRoute(rows, cols, horizontal, rand);

  const map = new Map();
  linkRun(map, trunk, MAJOR);

  // The two ends run OFF the map — a highway that stops dead at the border
  // reads as a mistake rather than as a road that came from somewhere.
  const head = trunk[0], tail = trunk[trunk.length - 1];
  link(map, head, horizontal ? 'w' : 'n', MAJOR);
  link(map, tail, horizontal ? 'e' : 's', MAJOR);

  // Pathways branch until nothing on the map is more than MAX_ROAD_DISTANCE
  // from a road. Anchors are drawn from the whole network, not just the trunk,
  // so later branches hang off earlier ones and the far corners are not
  // stranded. The budget is only a runaway guard — the distance goal is what
  // actually stops the loop.
  fillToDistance(map, rows, cols, rand, opts.maxDist ?? MAX_ROAD_DISTANCE, {
    budget: Math.round(rows * cols * (opts.coverage ?? 0.7)),
  });

  return map;
}

/**
 * Stamp the network onto the grid's rooms as `room.road` — a per-direction map
 * of ranks — plus `room.roadRank` for the tile's dominant rank. Kept on the
 * room so it persists with the save and is in hand wherever a tile is drawn.
 *
 * `block` names room types a pathway may run UP TO but not INTO: a track can
 * lead to the water's edge or to a campsite, but neither carries a road across
 * it, and neither draws one inside. The cell leaves the network; the neighbour
 * it was approached from keeps its pointer, so the road runs to the shared edge
 * and stops.
 *
 * `exclude` names types a pathway may not even approach — collapsed ground is
 * not a destination, so a track that dead-ends against it is pulled back.
 */
export function applyRoadNetwork(grid, rows, cols, prebuilt, block = [], exclude = []) {
  const net = prebuilt || generateRoadNetwork(rows, cols);
  const blocked = new Set(block);
  const banned = new Set(exclude);

  // A track leads up to the water's edge, or to the edge of a campsite, and
  // stops there — it does not carry on across the pond or through somebody's
  // camp. So the cell itself leaves the network and draws no road at all, while
  // the ONE neighbour it was approached from keeps pointing at it: that
  // neighbour runs its road to the shared edge and ends. Keeping a single
  // connection on the cell instead drew a stub of road onto the water.
  //
  // Neither type is ever laid on the trunk (see placeCampsite), so nothing here
  // can sever the through-route.
  for (const entry of [...net.values()]) {
    const room = grid?.[entry.row]?.[entry.col];
    if (!room || !blocked.has(room.type)) continue;
    const dirs = Object.keys(entry.conn);
    const approach = dirs.find(d => entry.conn[d] === MAJOR) || dirs[0];
    for (const dir of dirs) {
      if (dir === approach) continue;
      const [dr, dc] = DIRS[dir];
      const other = net.get(key(entry.row + dr, entry.col + dc));
      if (other) delete other.conn[OPPOSITE[dir]];
    }
    net.delete(key(entry.row, entry.col));
  }
  // Cells the network may not touch at all. Collapsed ground is not somewhere
  // a road stops, the way a campsite or a shoreline is — a track running up to
  // a slab of rubble and ending still draws a road onto a tile nobody can
  // enter. Drop the cell and unwire whatever pointed at it.
  const orphans = [];
  const drop = entry => {
    for (const dir of Object.keys(entry.conn)) {
      const [dr, dc] = DIRS[dir];
      const other = net.get(key(entry.row + dr, entry.col + dc));
      if (!other) continue;
      delete other.conn[OPPOSITE[dir]];
      orphans.push(other);
    }
    net.delete(key(entry.row, entry.col));
  };
  for (const entry of [...net.values()]) {
    const room = grid?.[entry.row]?.[entry.col];
    if (!room || !banned.has(room.type)) continue;
    // never sever the through-route; the trunk is laid as road rooms before
    // anything else is placed, so this should be unreachable
    if (entry.rank === MAJOR) continue;
    drop(entry);
  }
  // Pull the rest of that spur back with it, and take any other track that
  // dead-ends beside the rubble with it. A road that stopped one tile short is
  // the same complaint one tile over, and a spur only grew that way to reach
  // ground that is now unreachable. Pruning stops at the first cell still
  // holding two ways out, so junctions and through-routes survive.
  const pruneSpurs = () => {
    while (orphans.length) {
      const entry = orphans.pop();
      if (entry.rank === MAJOR) continue;
      if (!net.has(key(entry.row, entry.col))) continue;
      if (Object.keys(entry.conn).length > 1) continue;
      // a campsite or a shoreline is SUPPOSED to sit at the end of a track
      if (blocked.has(grid?.[entry.row]?.[entry.col]?.type)) continue;
      drop(entry);
    }
  };
  const seedFromBanned = () => {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (!banned.has(grid?.[row]?.[col]?.type)) continue;
        for (const [dr, dc] of Object.values(DIRS)) {
          const other = net.get(key(row + dr, col + dc));
          if (other) orphans.push(other);
        }
      }
    }
  };
  seedFromBanned();
  pruneSpurs();

  // a cell left with nothing to connect to is not on the network at all
  for (const [k, entry] of [...net.entries()]) {
    if (!Object.keys(entry.conn).length) net.delete(k);
  }

  // Cutting a pathway at a blocked or rest-stop cell can strand whatever lay
  // beyond it. Those cells still hold valid-looking links to each other, so
  // they would render as a length of road joined to nothing. Keep only what is
  // still reachable from the trunk.
  const roots = [...net.values()].filter(c => c.rank === MAJOR);
  const live = new Set(roots.map(c => key(c.row, c.col)));
  const queue = [...roots];
  while (queue.length) {
    const cur = queue.pop();
    for (const dir of Object.keys(cur.conn)) {
      const [dr, dc] = DIRS[dir];
      const k = key(cur.row + dr, cur.col + dc);
      const next = net.get(k);
      if (!next || live.has(k) || !next.conn[OPPOSITE[dir]]) continue;
      live.add(k);
      queue.push(next);
    }
  }
  for (const [k, entry] of [...net.entries()]) {
    if (live.has(k)) continue;
    for (const dir of Object.keys(entry.conn)) {          // unwire the neighbours first
      const [dr, dc] = DIRS[dir];
      const other = net.get(key(entry.row + dr, entry.col + dc));
      if (other) delete other.conn[OPPOSITE[dir]];
    }
    net.delete(k);
  }
  seedFromBanned();          // the sweep can leave a fresh dead end by the rubble
  pruneSpurs();

  // Everything above this point REMOVES road, and between them they can strand
  // whole quarters of a map — a spur cut back from a slab of rubble takes the
  // ground it served with it. The generator laid the network out against an
  // empty grid and never sees this one, so the coverage rule has to be restored
  // here, against the map as it finally stands.
  const offLimits = new Set();
  const beside = new Set();
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const type = grid?.[row]?.[col]?.type;
      if (!banned.has(type) && !blocked.has(type)) continue;
      offLimits.add(key(row, col));                          // may not be entered
      if (!banned.has(type)) continue;
      for (const [dr, dc] of Object.values(DIRS)) beside.add(key(row + dr, col + dc));
    }
  }
  fillToDistance(net, rows, cols, Math.random, MAX_ROAD_DISTANCE,
    { avoid: offLimits, noStopAt: beside });

  // A portal is a device somebody installed, not a clearing — so something
  // drove to it. Run a track right up onto the entry and extraction tiles,
  // last of all, where no later trimming can pull it back off again. The trunk
  // deliberately steers clear of both (they sit on opposite map edges and so do
  // its ends), which is exactly why they need connecting by hand.
  const taken = new Set([...net.keys(), ...offLimits]);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (!PORTAL_TYPES.has(grid?.[row]?.[col]?.type)) continue;
      if (net.has(key(row, col))) continue;
      joinToNetwork(net, rows, cols, Math.random, { row, col },
        { taken, avoid: offLimits, noStopAt: new Set(), relax: true, maxLen: rows + cols });
      // ...and if the greedy walk could not get there, find the way properly.
      // A portal boxed in by a campsite and a shoreline has no approach that
      // closes the gap on every step, so growToward reports nothing while an
      // obvious way round sits one tile out. Measured, that left a portal with
      // no road on three maps in a thousand.
      if (net.has(key(row, col))) continue;
      const run = pathToNetwork(net, rows, cols, { row, col }, taken);
      if (!run) continue;                    // genuinely walled in; nothing to be done
      linkRun(net, run, MINOR);
      for (const cell of run) taken.add(key(cell.row, cell.col));
    }
  }

  for (const { row, col, conn, rank } of net.values()) {
    const room = grid?.[row]?.[col];
    if (!room) continue;
    room.road = { n: conn.n || null, e: conn.e || null, s: conn.s || null, w: conn.w || null };
    room.roadRank = Object.values(conn).includes(MAJOR) ? MAJOR : rank;
  }

  // How far each tile sits from the nearest roadway, written alongside the road
  // itself and for the same reason: it has to be in hand wherever the tile is
  // drawn, and it has to survive a save. The renderer thins a recipe's
  // buildings with it, so frontage on a road is built up and the ground behind
  // it empties out — which is what settles along a route in the first place.
  //
  // Coverage keeps this within MAX_ROAD_DISTANCE for anywhere a team can walk;
  // the clamp is for the handful of cells a pathway is forbidden to reach at
  // all (collapsed ground), which are landmarks the renderer never thins.
  const dist = distanceField(rows, cols, (r, c) => net.has(key(r, c)));
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const room = grid?.[row]?.[col];
      if (room) room.roadDist = Math.min(dist[row][col], MAX_ROAD_DISTANCE);
    }
  }
  return net;
}
